import Image from 'next/image';
import { useMemo } from 'react';
import { TacoRating } from '@appsumo/dorado-react';
import Paragraphs from '@render-props/paragraphs';
import { UserMeta, Actions, Buttons } from '.';
import { Comment as CommentType } from '~/lib/discussions/types';
import DiscussionStatus from './DiscussionStatus';

export function Comment({
  href,
  comment,
  mutateComment,
  moderator = false,
  border = false,
  className = '',
}: {
  href: string;
  comment: CommentType;
  mutateComment: () => void;
  moderator?: boolean;
  border?: boolean;
  className?: string;
}) {
  const { user } = comment;
  const status: null | 'pending' | 'approved' | 'denied' = useMemo(() => {
    if (!moderator) {
      return null;
    }

    if (typeof comment.approved === 'undefined' || comment.approved === null) {
      return 'pending';
    }

    if (!comment.approved) {
      return 'denied';
    }

    return null;
  }, [moderator, comment.approved]);

  return (
    <div
      id={`${comment.id}`}
      className={`relative mt-8 flex flex-row gap-x-4 p-12 max-sm:p-4
      ${border ? 'rounded border border-gray-300' : ''}
      ${
        moderator && status === 'pending'
          ? 'rounded border border-sunset shadow-md shadow-sunset'
          : ''
      }
      ${
        moderator && status === 'denied'
          ? 'rounded border border-ready-red shadow-md shadow-ready-red'
          : ''
      }
      ${
        moderator && comment.resolved === false
          ? 'rounded border border-entrepurple shadow-md shadow-entrepurple'
          : ''
      }
      ${className}`}
    >
      {moderator && (status !== null || comment.resolved === false) && (
        <div className="absolute top-0 left-0 flex -translate-y-full gap-x-2">
          <DiscussionStatus
            isApproved={comment.approved}
            isResolved={comment.resolved}
          />
        </div>
      )}
      <div className="min-w-[70px] max-sm:min-w-[40px]">
        <Image
          src={user.avatar}
          alt={user.username}
          width={70}
          height={70}
          unoptimized
          className="h-[70px] w-[70px] rounded-full max-sm:h-[40px] max-sm:w-[40px]"
        />
      </div>
      <div className="shrink grow">
        <UserMeta
          user={comment.user}
          posted={comment.submit_date}
          purchased={comment.purchased}
        />
        <div>
          {comment.rating && (
            <TacoRating
              averageRating={comment.rating}
              href={href}
              height={16}
              className="mt-4"
            />
          )}
          <h5 className="my-4 font-bold">{comment.title}</h5>
          <Paragraphs text={comment.comment}>
            {({ key, text }: { key: number; text: string }) => (
              <p key={key} className={'my-4'}>
                {text}
              </p>
            )}
          </Paragraphs>
        </div>
        <Buttons comment={comment} mutateComment={mutateComment} />
        <Actions
          href={href}
          comment={comment}
          moderator={moderator}
          mutateComment={mutateComment}
        />
      </div>
    </div>
  );
}
